import { z } from 'zod';

export const configSchema = z.object({
  version: z.string(),

  document: z.object({
    title: z.string(),
    author: z.string(),
    date: z.union([z.string(), z.literal('auto')]),
    language: z.string(),
    keywords: z.array(z.string()),
    subject: z.string(),
  }),

  page: z.object({
    format: z.string(),
    orientation: z.enum(['portrait', 'landscape']),
    margins: z.object({
      top: z.string(),
      right: z.string(),
      bottom: z.string(),
      left: z.string(),
    }),
  }),

  features: z.object({
    toc: z.object({
      enabled: z.boolean(),
      depth: z.number().min(1).max(6),
      title: z.string(),
      position: z.string(),
    }),

    numbering: z.object({
      headings: z.boolean(),
      figures: z.boolean(),
      tables: z.boolean(),
      equations: z.boolean(),
    }),

    crossReferences: z.object({
      enabled: z.boolean(),
      prefix: z.object({
        figure: z.string(),
        table: z.string(),
        section: z.string(),
      }),
    }),

    math: z.object({
      enabled: z.boolean(),
      renderer: z.enum(['katex', 'mathjax']),
      macros: z.record(z.string()),
    }),

    diagrams: z.object({
      mermaid: z.boolean(),
      plantuml: z.boolean(),
    }),

    codeHighlight: z.object({
      enabled: z.boolean(),
      theme: z.string(),
      showLineNumbers: z.boolean(),
      languages: z.union([z.literal('auto'), z.array(z.string())]),
    }),

    callouts: z.object({
      enabled: z.boolean(),
      types: z.array(z.string()),
    }),

    citations: z.object({
      enabled: z.boolean(),
      style: z.string(),
      bibliography: z.string(),
    }),
  }),

  theme: z.string(),
  customCSS: z.array(z.string()),

  fonts: z.array(
    z.object({
      family: z.string(),
      files: z.array(
        z.object({
          path: z.string(),
          weight: z.number(),
          style: z.string(),
        })
      ),
    })
  ),

  header: z.object({
    enabled: z.boolean(),
    template: z.string(),
    height: z.string(),
  }),

  footer: z.object({
    enabled: z.boolean(),
    template: z.string(),
    height: z.string(),
  }),

  pdf: z.object({
    version: z.string(),
    embedFonts: z.boolean(),
    compression: z.boolean(),
    pdfA: z.boolean(),
    pdfUA: z.boolean(),
    tagged: z.boolean(),
    encryption: z.object({
      enabled: z.boolean(),
      userPassword: z.string(),
      ownerPassword: z.string(),
      permissions: z.object({
        print: z.boolean(),
        copy: z.boolean(),
        modify: z.boolean(),
      }),
    }),
  }),

  images: z.object({
    optimize: z.boolean(),
    quality: z.number().min(1).max(100),
    maxWidth: z.number(),
    embedRemote: z.boolean(),
    cache: z.boolean(),
    cacheDir: z.string(),
  }),

  processing: z.object({
    parallel: z.boolean(),
    maxWorkers: z.number().min(1),
    timeout: z.number(),
  }),

  output: z.object({
    directory: z.string(),
    filename: z.string(),
    overwrite: z.boolean(),
  }),

  plugins: z.array(
    z.object({
      name: z.string(),
      enabled: z.boolean(),
      options: z.record(z.any()).optional(),
    })
  ),

  dev: z.object({
    watch: z.boolean(),
    liveReload: z.boolean(),
    port: z.number(),
    open: z.boolean(),
  }),

  validation: z.object({
    markdown: z.object({
      enabled: z.boolean(),
      rules: z.string(),
    }),
    links: z.object({
      checkExternal: z.boolean(),
      checkInternal: z.boolean(),
    }),
    accessibility: z.object({
      enabled: z.boolean(),
      level: z.enum(['A', 'AA', 'AAA']),
    }),
  }),

  logging: z.object({
    level: z.enum(['error', 'warn', 'info', 'debug', 'trace']),
    file: z.string(),
    format: z.enum(['text', 'json']),
  }),
});

export type Configuration = z.infer<typeof configSchema>;
